/**
 * Image capture controls and the local preview of a stored attachment. Pure UI: the caller
 * decides where the file goes (always the local database — nothing here touches the network).
 */
import { useEffect, useRef, type ComponentType, type ReactNode } from "react";
import type { AttachmentOrigin } from "@/domain/attachments";
import type { Attachment } from "@/domain/model";
import { formatBytes } from "@/lib/format";
import { IconCamera, IconImage, type IconProps } from "./icons";
import { ConfirmButton } from "./primitives";

export type ImageFileHandler = (file: File, origin: AttachmentOrigin) => void | Promise<void>;

/**
 * A button-looking label around a visually hidden file input: tapping it opens the camera
 * (`capture="environment"`, rear camera; browsers without camera support fall back to the file
 * picker) or the photo library / file picker.
 */
export function ImageInput({
  origin,
  onFile,
  disabled,
  className = "",
  testId,
  children,
}: {
  origin: AttachmentOrigin;
  onFile: ImageFileHandler;
  disabled?: boolean;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <label className={`cursor-pointer ${disabled ? "pointer-events-none opacity-60" : ""} ${className}`}>
      <input
        type="file"
        accept="image/*"
        {...(origin === "camera" ? { capture: "environment" as const } : {})}
        className="sr-only"
        disabled={disabled}
        data-testid={testId}
        onChange={(event) => {
          const input = event.currentTarget;
          const file = input.files?.[0];
          // Reset so choosing the same file again still fires a change event.
          input.value = "";
          if (file) void onFile(file, origin);
        }}
      />
      {children}
    </label>
  );
}

const CAPTURE_CHOICES: { origin: AttachmentOrigin; title: string; icon: ComponentType<IconProps> }[] = [
  { origin: "camera", title: "Fotografa ricevuta", icon: IconCamera },
  { origin: "library", title: "Carica screenshot / foto", icon: IconImage },
];

/** The two capture buttons, side by side (used in the expense form). */
export function CaptureButtons({ onFile, disabled }: { onFile: ImageFileHandler; disabled?: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {CAPTURE_CHOICES.map(({ origin, title, icon: Icon }) => (
        <ImageInput
          key={origin}
          origin={origin}
          onFile={onFile}
          disabled={disabled}
          testId={`image-input-${origin}`}
          className="flex min-h-12 items-center justify-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-center text-sm font-semibold hover:bg-ground has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-petrol"
        >
          <Icon />
          {title}
        </ImageInput>
      ))}
    </div>
  );
}

/** Preview of a stored image, with its details and replace/remove actions. */
export function AttachmentPreview({
  attachment,
  onReplace,
  onRemove,
  busy,
}: {
  attachment: Attachment;
  /** Omitted when the image cannot be changed (e.g. a voided expense). */
  onReplace?: ImageFileHandler;
  onRemove?: () => void | Promise<void>;
  busy?: boolean;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  // The object URL points at bytes already in memory (from IndexedDB); it is revoked on change.
  useEffect(() => {
    const url = URL.createObjectURL(new Blob([attachment.data], { type: attachment.mimeType }));
    if (imgRef.current) imgRef.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  const original = attachment.original;
  const name = original.name ?? "Immagine senza nome";
  const converted = attachment.processing !== "keep";
  return (
    <figure className="flex flex-col gap-3 rounded-xl border border-line bg-ground p-3" data-testid="attachment-preview">
      <img
        ref={imgRef}
        alt={`Anteprima di ${name}`}
        width={attachment.width}
        height={attachment.height}
        className="mx-auto max-h-64 w-auto max-w-full rounded-lg bg-surface object-contain"
      />
      <figcaption className="text-sm">
        <p className="font-medium break-all" data-testid="attachment-name">
          {name}
        </p>
        <p className="text-muted" data-testid="attachment-details">
          {original.type || "tipo sconosciuto"} · {formatBytes(original.byteSize)} · {original.width}×{original.height}
          {converted ? ` → salvata come JPEG ${attachment.width}×${attachment.height}, ${formatBytes(attachment.byteSize)}` : ""}
        </p>
        <p className="mt-1 text-xs text-muted">Salvata solo su questo dispositivo: non viene caricata online.</p>
      </figcaption>
      {onReplace || onRemove ? (
        <div className="flex flex-wrap gap-2">
          {onReplace ? (
            <ImageInput
              origin="library"
              onFile={onReplace}
              disabled={busy}
              testId="image-input-replace"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-line bg-surface px-4 py-2.5 font-semibold hover:bg-ground has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-petrol"
            >
              Sostituisci immagine
            </ImageInput>
          ) : null}
          {onRemove ? <ConfirmButton label="Rimuovi immagine" confirmLabel="Conferma rimozione" onConfirm={onRemove} disabled={busy} /> : null}
        </div>
      ) : null}
    </figure>
  );
}
